import React, { useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as S from "../../components/Modal/Modal.styles";
import * as UserStyles from "./UserDetails.styles";
import {
  getUser,
  inactivateUser,
  promoteUserToAdmin,
  demoteUserFromAdmin,
} from "../../api";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { getUserRole } from "../../helpers";
import { useLogoutAllDevices } from "../../hooks/useLogoutAllDevices";
import { useImpersonate } from "../../hooks/useImpersonate";
import { IMPERSONATE_TEXTS } from "../../constants/impersonate";
import { StatusCodes } from "http-status-codes";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../constants/FeatureFlag";
import ModalConfirm from "../../components/ModalConfirm";

const MODAL_TYPES = {
  INACTIVATE: "inactivate",
  PROMOTE: "promote",
  DEMOTE: "demote",
};

const UserDetails = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [selectedModalType, setSelectedModalType] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [user, setUser] = useState(null);

  const loggedAdminId = useSelector((state) => state.auth.user?.id);

  const { isFeatureFlagEnable: isAdminRoleFlagEnabled } = useFeatureFlag(
    FEATURE_FLAGS.PERMISSION.ADMIN_ROLE_ASSIGNMENT
  );

  const isAdminRoleAssignmentEnabled =
    isAdminRoleFlagEnabled ||
    process.env.REACT_APP_FORCE_ADMIN_ROLE_ASSIGNMENT === "true";

  const {
    showModal: showLogoutModal,
    setShowModal: setShowLogoutModal,
    openModal,
    closeModal,
    loading: logoutLoading,
    error: logoutError,
    handleLogout: handleLogoutAllDevices,
  } = useLogoutAllDevices(id);

  const { impersonate, isLoading: impersonateLoading } = useImpersonate();

  const isInactivable = user?.status === "active" && user?.role !== "client" && user?.role !== "admin";

  const isPromotable =
    isAdminRoleAssignmentEnabled &&
    user?.role === "client" &&
    user?.status === "active";

  const isDemotable =
    isAdminRoleAssignmentEnabled &&
    user?.role === "admin" &&
    user?.status === "active" &&
    user.id !== loggedAdminId;

  const clientId = user?.client_id || user?.parent_client_id;

  const closeSelectedModal = () => setSelectedModalType(null);

  const formatIsoToDateBr = (isoString) => {
    if (!isoString) return "";

    try {
      return format(parseISO(isoString), "dd/MM/yyyy");
    } catch (error) {
      toast.error("Data inválida");
      return "";
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await getUser(id);
      setUser(response.data);
    } catch (error) {
      toast.error("Erro ao buscar usuário.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleInactivateUser = async () => {
    try {
      await inactivateUser(id);
    } catch (error) {
      toast.error("Erro ao inativar o usuário.");
      console.error({error});
    } finally {
      fetchUser();
      closeSelectedModal();
    }
  };

  const handlePromoteUserToAdmin = async () => {
    setIsModalLoading(true);
    try {
      await promoteUserToAdmin(id);
      toast.success("Usuário promovido a administrador com sucesso!");
      closeSelectedModal();
      await fetchUser();
    } catch (error) {
      const status = error?.response?.status;

      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          toast.error("Você não tem permissão para realizar esta ação.");
          break;
        case StatusCodes.FORBIDDEN:
          toast.error("Você não tem permissões suficientes ou o usuário não é do role Principal.");
          break;
        case StatusCodes.NOT_FOUND:
          toast.error("Funcionalidade indisponível ou usuário não encontrado.");
          break;
        case StatusCodes.CONFLICT:
          toast.error("Usuário já é administrador.");
          break;
        default:
          toast.error("Não foi possível promover o usuário. Tente novamente.");
      }

      closeSelectedModal();
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleDemoteUserFromAdmin = async () => {
    setIsModalLoading(true);
    try {
      await demoteUserFromAdmin(id);
      toast.success("Administrador despromovido com sucesso!");
      closeSelectedModal();
      await fetchUser();
    } catch (error) {
      const status = error?.response?.status;

      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          toast.error("Você não tem permissão para realizar esta ação.");
          break;
        case StatusCodes.FORBIDDEN:
          toast.error("Não é permitido despromover a si mesmo.");
          break;
        case StatusCodes.NOT_FOUND:
          toast.error("Funcionalidade indisponível ou usuário não encontrado.");
          break;
        case StatusCodes.CONFLICT:
          toast.error("Este usuário não é administrador.");
          break;
        default:
          toast.error("Não foi possível despromover o usuário. Tente novamente.");
      }

      closeSelectedModal();
    } finally {
      setIsModalLoading(false);
    }
  };

  const onNavigateToClient = () => {
    if (clientId) {
      window.open(`/clientes/${clientId}`, "_blank");
    }
  };

  const handleImpersonateClick = () => {
    impersonate({ userId: id });
  };

  const renderUserInfo = () => {
    if (!user) return null;

    return (
      <UserStyles.Container>
        <UserStyles.InfoRow>
          <UserStyles.Label>ID:</UserStyles.Label>
          <UserStyles.Value>{user.id}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Nome:</UserStyles.Label>
          <UserStyles.Value>{user.name}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Função:</UserStyles.Label>
          <UserStyles.Value>{getUserRole(user.role)}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Email:</UserStyles.Label>
          <UserStyles.Value>{user.email}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Status de verificação:</UserStyles.Label>
          <UserStyles.Value>{user.verified ? "Verificado" : "Não verificado"}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Data de criação:</UserStyles.Label>
          <UserStyles.Value>{formatIsoToDateBr(user.created_at)}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Última modificação:</UserStyles.Label>
          <UserStyles.Value>{formatIsoToDateBr(user.modified_at)}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Fonte de registro:</UserStyles.Label>
          <UserStyles.Value>{user.registration_source}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Cidade de referência:</UserStyles.Label>
          <UserStyles.Value>{user.reference_city ? user.reference_city : "Não informado"}</UserStyles.Value>
        </UserStyles.InfoRow>
        <UserStyles.InfoRow>
          <UserStyles.Label>Permissão para usar JusDecision:</UserStyles.Label>
          <UserStyles.Value>{user.may_use_jusdecision ? "Permitido" : "Não permitido"}</UserStyles.Value>
        </UserStyles.InfoRow>

        <UserStyles.ButtonWrapper>
          {clientId ?  
            <UserStyles.Button onClick={onNavigateToClient}>
              Acessar Cliente
            </UserStyles.Button>
            : null}

          <UserStyles.Button
            onClick={handleImpersonateClick}
            disabled={impersonateLoading}
          >
            {IMPERSONATE_TEXTS.BUTTON_LABEL}
          </UserStyles.Button>

          <UserStyles.Button color="secondary" onClick={openModal}>
            Deslogar de Todos Dispositivos
          </UserStyles.Button>

          {isInactivable &&
            <UserStyles.Button color="secondary" onClick={() => setSelectedModalType(MODAL_TYPES.INACTIVATE)}>
              Inativar Usuário
            </UserStyles.Button>
          }

          {isPromotable &&
            <UserStyles.Button onClick={() => setSelectedModalType(MODAL_TYPES.PROMOTE)}>
              Promover a Administrador
            </UserStyles.Button>
          }

          {isDemotable &&
            <UserStyles.Button color="secondary" onClick={() => setSelectedModalType(MODAL_TYPES.DEMOTE)}>
              Despromover Administrador
            </UserStyles.Button>
          }
        </UserStyles.ButtonWrapper>
      </UserStyles.Container>
    );
  };

  return (
    <BlockUi tag="div" blocking={loading}>
      {user && (
        <UserStyles.Card>
          <UserStyles.Header>Detalhes do Usuário</UserStyles.Header>
          {renderUserInfo()}
        </UserStyles.Card>
      )}

      {selectedModalType === MODAL_TYPES.INACTIVATE && (
        <S.Modal>
          <S.ModalContent>
            <S.ModalTitle>Confirmação</S.ModalTitle>
            <S.Text>Tem certeza que deseja inativar este usuário?</S.Text>
            <S.ButtonRow>
              <S.ConfirmButton onClick={handleInactivateUser}>
                Confirmar
              </S.ConfirmButton>
              <S.CancelButton onClick={closeSelectedModal}>
                Cancelar
              </S.CancelButton>
            </S.ButtonRow>
          </S.ModalContent>
        </S.Modal>
      )}

      {selectedModalType === MODAL_TYPES.PROMOTE && (
        <ModalConfirm
          title="Confirmação"
          message="Tem certeza que deseja promover este usuário a administrador?"
          onConfirm={handlePromoteUserToAdmin}
          onClose={closeSelectedModal}
          isLoading={isModalLoading}
        />
      )}

      {selectedModalType === MODAL_TYPES.DEMOTE && (
        <ModalConfirm
          title="Confirmação"
          message="Tem certeza que deseja despromover este administrador? Ele perderá o acesso ao painel admin."
          onConfirm={handleDemoteUserFromAdmin}
          onClose={closeSelectedModal}
          isLoading={isModalLoading}
        />
      )}

      {showLogoutModal && (
        <S.Modal>
          <S.ModalContent>
            <S.ModalTitle>Confirmação</S.ModalTitle>
            <S.Text>Tem certeza que deseja deslogar este usuário de todos os dispositivos?</S.Text>
            {logoutError && (
              <div style={{ color: 'red', marginTop: '10px', marginBottom: '10px' }}>
                {logoutError}
              </div>
            )}
            <S.ButtonRow>
              <S.ConfirmButton 
                onClick={handleLogoutAllDevices}
                disabled={logoutLoading}
              >
                {logoutLoading ? "Processando..." : "Confirmar"}
              </S.ConfirmButton>
              <S.CancelButton 
                onClick={closeModal}
                disabled={logoutLoading}
              >
                Cancelar
              </S.CancelButton>
            </S.ButtonRow>
          </S.ModalContent>
        </S.Modal>
      )}
    </BlockUi>
  );
};

export default UserDetails;
